import { useEffect, useState } from 'react';
import type { TestSummary } from '../api/types';
import { testCode } from '../api/endpoints';
import { describeError } from '../api/client';
import { useToast } from '../state';
import { Modal } from './Form';
import { LoadingRows, ErrorNotice } from './Feedback';
import { CopyButton } from './CopyButton';

export interface ExportPlaywrightModalProps {
  test: TestSummary;
  onClose: () => void;
}

export function ExportPlaywrightModal({ test, onClose }: ExportPlaywrightModalProps): JSX.Element {
  const toast = useToast();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fileSlug = (test.name || 'test')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const fileName = `${fileSlug || test.id}.spec.ts`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    testCode(test.id)
      .then((source) => {
        if (!cancelled) {
          setCode(source);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(describeError(err));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [test.id]);

  const handleDownload = () => {
    if (!code) return;
    const blob = new Blob([code], { type: 'text/typescript;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${fileName}`);
  };

  return (
    <Modal title={`Export as Playwright: ${fileName}`} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Clean, standalone Playwright TypeScript test script generated from this test plan. Run it locally or in CI with zero vendor lock-in via <code>npx playwright test {fileName}</code>.
        </p>

        {loading ? <LoadingRows rows={4} label="Synthesizing Playwright script…" /> : null}
        {error ? <ErrorNotice error={new Error(error)} /> : null}

        {code ? (
          <>
            <div
              style={{
                position: 'relative',
                background: '#090d16',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '12px 14px',
                maxHeight: '440px',
                overflowY: 'auto',
              }}
            >
              <div style={{ position: 'absolute', top: 10, right: 10 }}>
                <CopyButton value={code} label="Copy Script" />
              </div>

              <pre
                style={{
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: '#e2e8f0',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {code}
              </pre>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span className="muted" style={{ fontSize: 12 }}>
                Target: Playwright Chromium (<code>@playwright/test</code>)
              </span>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn--small btn--ghost" onClick={onClose}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn--small btn--primary"
                  onClick={handleDownload}
                >
                  💾 Download {fileName}
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
